import { type ScriptShot, postTelemetry, submitCapture } from '@/api/capture';
import { fingerprintForSubmit } from '@/media/useCaptureIntegrity';
import { SvApiError } from '@/api/errors';
import type { CaptureSession } from '@/capture/captureSession';
import { GATE_VERSION } from '@/capture/thresholds';
import { useT } from '@/i18n/runtime';
import { shotText } from '@/i18n/serverText';
import type { GeoPermissionReport } from '@/media/useGeoEvidence';
import { useCaptureStore } from '@/store/captureStore';
import type { TenantConfig } from '@/theme/tenant';
import { purgeVisit } from '@/upload/chunkQueue';
import { useEffect, useState } from 'react';
import { AppBar } from './components/AppBar';
import { Button } from './components/Button';
import { Icon } from './components/Icon';
import { SavedWillSend } from './components/SavedWillSend';
import { useHelpStep } from './help/HelpContext';

/**
 * The be names missing shots by shotId — in `details.missing` when it sends details, else comma-joined
 * in the message (`SvCaptureException(422, code, String.join(",", missing))`). Accept both, never neither.
 *
 * A shotId is OPAQUE (be rule): never parse its separator or shape. The only filter applied is
 * "no whitespace" — a shotId is a URL path segment, so a token with spaces is prose, not an id.
 */
export function parseMissing(e: SvApiError): string[] {
  const d = e.details as { missing?: unknown } | null;
  if (Array.isArray(d?.missing)) return (d.missing as unknown[]).filter((x): x is string => typeof x === 'string' && x.trim() !== '');
  return e.message.split(',').map((x) => x.trim()).filter((x) => x !== '' && !/\s/.test(x));
}

/**
 * Review & submit. Waits for the background queue to drain (never blocks capture — this screen
 * only exists once nothing is left to record), then `POST /submit`. A 422
 * SV_MANDATORY_SHOT_MISSING / SV_CHALLENGE_UNANSWERED is surfaced by prompt name and the flow is
 * sent back to those shots (they are dropped from the local done-set, so the ordinal walk lands on
 * them). The submit-time telemetry carries the whole-visit IMU summary, the queue persistence mode,
 * the signature availability and the gate version — the reviewer's context, never a verdict.
 */
export function ReviewSubmit({
  tenant,
  session,
  active,
  geoPermission,
  onSubmitted,
}: {
  tenant: TenantConfig;
  session: CaptureSession;
  active: readonly ScriptShot[];
  geoPermission: GeoPermissionReport;
  onSubmitted: () => void;
}): JSX.Element {
  const { t, locale } = useT();
  const completed = useCaptureStore((s) => s.completed);
  const sealedLocal = useCaptureStore((s) => s.sealedLocal);
  const refused = useCaptureStore((s) => s.refused);
  const captureState = useCaptureStore((s) => s.captureState);
  const pending = useCaptureStore((s) => s.pendingChunks);
  const skipped = useCaptureStore((s) => s.skipped);
  const skipReasons = useCaptureStore((s) => s.skipReasons);
  const submitMissing = useCaptureStore((s) => s.submitMissing);
  const persistence = useCaptureStore((s) => s.persistence);
  const signature = useCaptureStore((s) => s.signature);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [idle, setIdle] = useState(false);
  useHelpStep({ screen: 'review', tenantName: tenant.name });

  useEffect(() => {
    let alive = true;
    void session.uploader.whenIdle().then(() => {
      if (alive) setIdle(true);
    });
    return () => {
      alive = false;
    };
  }, [session, pending]);

  const serverDone = new Set([...(captureState?.completedShots ?? []), ...completed]);
  const uploading = active.filter((s) => sealedLocal.includes(s.shotId) && !serverDone.has(s.shotId));
  const ready = idle && uploading.length === 0 && pending === 0;

  const submit = async (): Promise<void> => {
    if (busy || !ready) return;
    setBusy(true);
    setFailed(false);
    try {
      try {
        const fingerprint = fingerprintForSubmit();
        await postTelemetry({
          // the cached capture fingerprint (never a new collection); with none, the field is omitted — `null` here
          // used to overwrite the last shot's fingerprint with nothing
          ...(fingerprint !== undefined ? { fingerprint } : {}),
          imuSummary: session.imu.all(),
          permissions: { geolocation: geoPermission, motion: session.imu.permission, queuePersistence: persistence, signature: signature.state === 'unavailable' ? signature.reason : signature.state, gateVersion: GATE_VERSION },
          clientTime: new Date().toISOString(),
        });
      } catch {
        /* telemetry is context, never a gate on submission */
      }
      const res = await submitCapture();
      useCaptureStore.getState().setReceipt(res.receiptRef);
      useCaptureStore.getState().setSubmitMissing([]);
      // DEC-097 T2 — the receipt is the purge point. The server has the whole visit (submit only
      // becomes possible once every shot is server-acked and the queue is drained), so the raw
      // masters buffered on this phone — the interior of someone's home, on what may be a borrowed
      // handset — have no further purpose. They go now, before the receipt screen, because the
      // served notice promises exactly that ("cleared from the phone once you have sent them",
      // DEC-097 T6). A failed or partial submit purges NOTHING: DEC-097 authorises the purge at the
      // receipt and nowhere else, and those bytes are still the resume.
      await purgeVisit(session.storage, session.visitId);
      onSubmitted();
    } catch (e) {
      if (e instanceof SvApiError && (e.code === 'SV_MANDATORY_SHOT_MISSING' || e.code === 'SV_CHALLENGE_UNANSWERED')) {
        useCaptureStore.getState().setSubmitMissing(parseMissing(e));
        return;
      }
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const goRecord = (refs: readonly string[]): void => {
    const ids = active.filter((s) => refs.includes(s.shotId) || refs.includes(s.promptKey)).map((s) => s.shotId);
    useCaptureStore.getState().unsealLocal(ids);
    useCaptureStore.getState().unskip(ids);
    useCaptureStore.getState().setSubmitMissing([]);
  };

  const statusOf = (s: ScriptShot): { key: string; tone: 'done' | 'pending' | 'refused' | 'skipped' } => {
    if (refused[s.shotId]) return { key: 'sv.review.item.refused', tone: 'refused' };
    if (serverDone.has(s.shotId)) return { key: 'sv.review.item.done', tone: 'done' };
    if (sealedLocal.includes(s.shotId)) return { key: 'sv.review.item.pending', tone: 'pending' };
    return { key: 'sv.review.item.skipped', tone: 'skipped' };
  };

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('sv.subtitle')} />
      <div className="stack">
        <div>
          <div className="h-title sm">{t('sv.review.title')}</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>{t('sv.review.sub')}</p>
        </div>

        {submitMissing.length > 0 ? (
          <div className="card pad sv-refused" role="alert" data-testid="submit-missing">
            <div className="pt">{t('sv.review.missing.title')}</div>
            <div className="pd">{t('sv.review.missing.body')}</div>
            <ul className="sv-capture-list">
              {submitMissing.map((k) => {
                const shot = active.find((s) => s.shotId === k || s.promptKey === k);
                return <li key={k} dir="auto">{shot ? shotText(shot, locale).text : k}</li>;
              })}
            </ul>
            <Button onClick={() => goRecord(submitMissing)} data-testid="record-missing">{t('sv.review.goTo')}</Button>
          </div>
        ) : null}

        <ul className="sv-review-list" data-testid="review-list">
          {active.map((s) => {
            const st = statusOf(s);
            return (
              <li key={s.shotId} className={`sv-review-item is-${st.tone}`} data-testid={`review-${s.shotId}`} data-tone={st.tone}>
                <span className="sv-review-icon">
                  <Icon name={st.tone === 'done' ? 'check' : st.tone === 'refused' ? 'info' : st.tone === 'pending' ? 'upload' : 'clock'} size={14} />
                </span>
                <span className="sv-review-text" dir="auto">
                  {shotText(s, locale).text}
                  {st.tone === 'skipped' && skipReasons[s.shotId] ? (
                    <span className="pd" style={{ display: 'block' }} data-testid={`review-skip-reason-${s.shotId}`}>
                      {t('sv.review.skipReason', { reason: t(`sv.help.skip.reason.${skipReasons[s.shotId]}`) })}
                    </span>
                  ) : null}
                </span>
                <span className="sv-review-status mono">{t(st.key)}</span>
                {st.tone === 'refused' || (st.tone === 'skipped' && !skipped.includes(s.shotId)) ? (
                  <button type="button" className="sv-link" onClick={() => goRecord([s.shotId])}>{t('sv.review.goTo')}</button>
                ) : null}
              </li>
            );
          })}
        </ul>

        <SavedWillSend />
        {!ready ? <p className="sub-sm" role="status">{t('sv.review.waiting')}</p> : null}
        {persistence === 'memory' ? <p className="sub-sm" role="note">{t('sv.done.memory')}</p> : null}
        {failed ? <div className="consent-note mono" role="alert">{t('sv.review.submitFail')}</div> : null}
      </div>

      <div className="spacer" />
      <div className="stack-sm sv-sticky-cta" style={{ marginTop: 16 }}>
        <Button onClick={() => void submit()} disabled={!ready || busy} data-testid="submit">{t('sv.action.submit')}</Button>
      </div>
    </div>
  );
}
